import { Component } from '@theme/component';

/**
 * Indéscents — quiz « Trouve ton parfum ».
 * 4 questions → score sur les 55 parfums (famille, moment, saison, intensité) → 3 résultats expliqués.
 * Les données viennent du <script type="application/json" data-parfums> rendu par Liquid.
 */
const POIDS = { famille: 4, moment: 3, saison: 2, intensite: 2 };

class IndescentsQuiz extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.nbResults = parseInt(this.dataset.results || '3', 10) || 3;
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.bar = this.querySelector('[data-bar]');
    this.res = this.querySelector('[data-res]');
    this.cards = this.querySelector('[data-cards]');
    this.resSub = this.querySelector('[data-res-sub]');
    this.backBtn = this.querySelector('[data-back]');
    this.skipBtn = this.querySelector('[data-skip]');
    this.nav = this.querySelector('.ind-quiz__nav');
    this.reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    try {
      this.parfums = JSON.parse(this.querySelector('[data-parfums]')?.textContent || '[]');
    } catch (e) {
      this.parfums = [];
    }

    this.answers = {};
    this.index = 0;

    this.querySelectorAll('[data-a]').forEach((btn) => {
      btn.addEventListener('click', () => this.answer(btn.dataset.a, btn.dataset.v, btn));
    });
    this.backBtn?.addEventListener('click', () => this.go(this.index - 1));
    this.skipBtn?.addEventListener('click', () => this.next());
    this.querySelector('[data-restart]')?.addEventListener('click', () => this.restart());

    this.go(0);
  }

  go(i) {
    this.index = Math.max(0, Math.min(i, this.steps.length - 1));
    this.steps.forEach((s, k) => { s.hidden = k !== this.index; });
    if (this.res) this.res.hidden = true;
    if (this.nav) this.nav.hidden = false;
    if (this.backBtn) this.backBtn.hidden = this.index === 0;
    if (this.skipBtn) this.skipBtn.hidden = false;
    this.setBar((this.index / this.steps.length) * 100);
  }

  setBar(pct) { if (this.bar) this.bar.style.width = `${pct}%`; }

  answer(cle, valeur, btn) {
    this.answers[cle] = valeur ? valeur.split('|').filter(Boolean) : [];
    btn?.blur();
    this.next();
  }

  next() {
    if (this.index >= this.steps.length - 1) { this.finish(); return; }
    this.go(this.index + 1);
  }

  /** Score d'un parfum face aux réponses ; renvoie aussi la raison principale, affichée sur la carte. */
  score(p) {
    let pts = 0;
    const raisons = [];
    const fam = this.answers.famille || [];
    if (fam.length && p.f && fam.includes(p.f)) { pts += POIDS.famille; raisons.push(`famille ${p.f.toLowerCase()}`); }
    const mom = this.answers.moment || [];
    if (mom.length && Array.isArray(p.m) && mom.some((x) => p.m.includes(x))) { pts += POIDS.moment; raisons.push(mom[0].toLowerCase()); }
    const sai = this.answers.saison || [];
    if (sai.length && Array.isArray(p.s) && sai.some((x) => p.s.includes(x))) { pts += POIDS.saison; raisons.push(sai[0].toLowerCase()); }
    else if (!sai.length && Array.isArray(p.s) && p.s.length >= 3) { pts += 1; raisons.push('toutes saisons'); }
    const inten = this.answers.intensite || [];
    if (inten.length) {
      const cible = parseInt(inten[0], 10);
      const ecart = Math.abs((parseInt(p.in, 10) || 3) - cible);
      if (ecart === 0) { pts += POIDS.intensite; raisons.push('sillage pile comme tu veux'); }
      else if (ecart === 1) { pts += 1; }
      else { pts -= 1; }
    }
    return { pts, raisons };
  }

  finish() {
    this.steps.forEach((s) => { s.hidden = true; });
    if (this.nav) this.nav.hidden = true;
    this.setBar(100);

    const classes = this.parfums
      .map((p) => ({ p, ...this.score(p) }))
      .sort((a, b) => b.pts - a.pts)
      .slice(0, this.nbResults);

    if (this.cards) {
      this.cards.innerHTML = '';
      classes.forEach((r, i) => {
        const a = document.createElement('a');
        a.className = 'ind-quiz__card';
        a.href = r.p.u;
        const rang = i === 0 ? 'Le plus proche' : `N°${i + 1}`;
        const pourquoi = r.raisons.length ? `Choisi pour : ${r.raisons.slice(0, 2).join(', ')}.` : '';
        const prix = r.p.p && r.p.p2 && r.p.p !== r.p.p2 ? `20 ml ${r.p.p} · 50 ml ${r.p.p2}` : (r.p.p || '');
        a.innerHTML = `
          <span class="ind-quiz__rank">${rang}</span>
          ${r.p.i ? `<img src="${r.p.i}" alt="" width="200" height="250" loading="lazy" decoding="async">` : ''}
          <span class="ind-quiz__cbody">
            <span class="ind-quiz__cname"></span>
            <span class="ind-quiz__cinsp"></span>
            <span class="ind-quiz__cwhy"></span>
            <span class="ind-quiz__cprice"></span>
          </span>`;
        // textContent : les données produit ne sont jamais injectées en HTML
        a.querySelector('.ind-quiz__cname').textContent = r.p.n;
        a.querySelector('.ind-quiz__cinsp').textContent = r.p.r ? `Inspiré de ${r.p.r}` : '';
        a.querySelector('.ind-quiz__cwhy').textContent = pourquoi;
        a.querySelector('.ind-quiz__cprice').textContent = prix;
        this.cards.appendChild(a);
      });
    }

    if (this.resSub) {
      const bits = [];
      if (this.answers.famille?.length) bits.push(this.answers.famille.join(' / ').toLowerCase());
      if (this.answers.moment?.length) bits.push(this.answers.moment[0].toLowerCase());
      if (this.answers.saison?.length) bits.push(this.answers.saison[0].toLowerCase());
      this.resSub.textContent = bits.length
        ? `D'après tes réponses : ${bits.join(', ')}. Commence par le 20 ml à 19 € si tu hésites.`
        : 'Commence par le 20 ml à 19 € si tu hésites — tu passes au 50 ml quand tu es sûr·e.';
    }

    if (this.res) {
      this.res.hidden = false;
      this.res.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  restart() {
    this.answers = {};
    if (this.res) this.res.hidden = true;
    this.go(0);
    this.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('indescents-quiz')) customElements.define('indescents-quiz', IndescentsQuiz);
